import React, { useState, useEffect, useCallback } from 'react';
import AdminNavbar from '../Navbars/AdminNavbar';
import AdminSidebar from '../Sidebar/AdminSidebar';
import { RiderApis } from '../../apis/RiderApis';
import { SuperAdminApis } from '../../apis/superAdminApi';
import { AxiosResponse } from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Oval } from 'react-loader-spinner';
import { useSelector } from 'react-redux';
//@ts-ignore
import Modal from 'react-awesome-modal';
import { SvgElement, icontypesEnum } from '../assets/svgElement';

function FleetRiders() {
    const userLoginData = useSelector((state: any) => state.data.login.value);

    const [riders, setRiders] = useState<any>([]);
    const [stats, setStats] = useState<any>(null);
    const [loader, setLoader] = useState(false);
    const [actionLoader, setActionLoader] = useState(false);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [branch, setBranch] = useState<any>([]);

    // Add rider modal
    const [addModal, setAddModal] = useState(false);
    const [riderForm, setRiderForm] = useState({
        first_name: '', last_name: '', email: '', phone_number: '',
        password: '', confirm_password: '',
        rider_town: '', state_of_residence: '', residential_address: '',
        date_of_birth: '', gender: '', nin: '', lga_of_residence: '',
    });

    // Detail modal
    const [detailModal, setDetailModal] = useState(false);
    const [riderDetail, setRiderDetail] = useState<any>(null);
    const [detailLoader, setDetailLoader] = useState(false);
    const [detailRiderId, setDetailRiderId] = useState<number | null>(null);
    const [paymentPageLoader, setPaymentPageLoader] = useState(false);

    // ── Load data ──────────────────────────────────────────────
    const loadRiders = useCallback((page?: string) => {
        setLoader(true);
        const query: any = {
            search,
            status: statusFilter !== 'all' ? statusFilter : '',
            page: page || '1',
            per_page: '15',
        };

        RiderApis.getFleetRiders(query).then((response: AxiosResponse<any>) => {
            setLoader(false);
            if (response?.data?.status) {
                setRiders(response.data.data);
            }
        }).catch(() => { setLoader(false); toast.error('Failed to load riders.'); });
    }, [search, statusFilter]);

    const loadStats = () => {
        RiderApis.getFleetStats().then((response: AxiosResponse<any>) => {
            if (response?.data?.status) setStats(response.data.data);
        });
    };

    const loadBranches = () => {
        const query = { search: '' };
        SuperAdminApis.getAllBranch('', query).then((response: AxiosResponse<any>) => {
            if (response?.data) setBranch(response?.data?.data);
        });
    };

    useEffect(() => {
        loadRiders();
        loadStats();
        loadBranches();
    }, []);

    // ── Form handling ──────────────────────────────────────────
    const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setRiderForm({ ...riderForm, [name]: value });
    };

    const resetForm = () => {
        setRiderForm({
            first_name: '', last_name: '', email: '', phone_number: '',
            password: '', confirm_password: '',
            rider_town: '', state_of_residence: '', residential_address: '',
            date_of_birth: '', gender: '', nin: '', lga_of_residence: '',
        });
    };

    // ── Create rider ───────────────────────────────────────────
    const handleCreateRider = useCallback((e: React.FormEvent) => {
        e.preventDefault();

        if (!riderForm.first_name || !riderForm.last_name || !riderForm.email || !riderForm.phone_number || !riderForm.password || !riderForm.rider_town || !riderForm.state_of_residence || !riderForm.residential_address) {
            toast.error('Please fill in all required fields.');
            return;
        }

        if (riderForm.password !== riderForm.confirm_password) {
            toast.error('Passwords do not match.');
            return;
        }

        if (riderForm.password.length < 6) {
            toast.error('Password must be at least 6 characters.');
            return;
        }

        setActionLoader(true);

        const formData = new FormData();
        Object.keys(riderForm).forEach(key => {
            const value = (riderForm as any)[key];
            if (value) formData.append(key, value);
        });

        RiderApis.createFleetRider(formData).then((response: AxiosResponse<any>) => {
            setActionLoader(false);
            if (response?.data?.status) {
                toast.success(response.data.message);
                setAddModal(false);
                resetForm();
                loadRiders();
                loadStats();
            } else {
                toast.error(response?.data?.message || 'Failed to create rider.');
            }
        }).catch((error: any) => {
            setActionLoader(false);
            if (error.response?.data?.errors) {
                const errors = error.response.data.errors;
                Object.keys(errors).forEach(key => toast.error(errors[key][0]));
            } else {
                toast.error(error.response?.data?.message || 'Failed to create rider.');
            }
        });
    }, [riderForm]);

    // ── View rider detail ──────────────────────────────────────
    const viewDetail = (riderId: number) => {
        setDetailLoader(true);
        setDetailModal(true);
        setDetailRiderId(riderId);
        RiderApis.getFleetRiderDetail(riderId, { per_page: 10 }).then((response: AxiosResponse<any>) => {
            setDetailLoader(false);
            if (response?.data?.status) {
                setRiderDetail(response.data.data);
            }
        }).catch(() => { setDetailLoader(false); toast.error('Failed to load rider details.'); });
    };

    const loadDetailPayments = (riderId: number, page: number) => {
        setPaymentPageLoader(true);
        RiderApis.getFleetRiderDetail(riderId, { per_page: 10, page }).then((response: AxiosResponse<any>) => {
            setPaymentPageLoader(false);
            if (response?.data?.status) {
                setRiderDetail((prev: any) => ({
                    ...prev,
                    payments: response.data.data.payments,
                }));
            }
        }).catch(() => { setPaymentPageLoader(false); });
    };

    // ── Pagination ─────────────────────────────────────────────
    const paginate = (label: string) => {
        const page = label === 'Next &raquo;' || label === '&laquo; Previous'
            ? riders?.links?.find((l: any) => l.label === label)?.url?.split('page=')[1]
            : label;
        if (page) loadRiders(page);
    };

    const formatAmount = (amount: any) => '₦' + Number(amount || 0).toLocaleString('en-NG', { minimumFractionDigits: 2 });

    const getStatusBadge = (status: string) => {
        const colors: any = {
            pending_review: 'bg-yellow-100 text-yellow-700',
            approved: 'bg-green-100 text-green-700',
            rejected: 'bg-red-100 text-red-700',
            pending_verification: 'bg-purple-100 text-purple-700',
        };
        return colors[status] || 'bg-gray-100 text-gray-600';
    };

    return (
        <>
            <AdminSidebar title="My Riders" />
            <div className="relative md:ml-64 bg-[#FAFAFA]">
                <AdminNavbar title="My Riders" />

                <div className="lg:px-10 pb-6 px-6 bg-[#FAFAFA] min-h-screen">
                    <div className="flex justify-between items-center mt-5 mb-4">
                        <div>
                            <h3 className="text-[15px] text-[#1C1C1C] font-semibold">My Riders</h3>
                            <p className="text-xs text-gray-500">Manage riders under your fleet</p>
                        </div>
                        <div className="flex gap-2">
                            <button onClick={() => window.location.href = '/rider/wallet'} className="border border-[#E04343] text-[#E04343] px-4 py-2 rounded-md text-sm font-semibold">
                                My Wallet
                            </button>
                            <button onClick={() => { resetForm(); setAddModal(true); }} className="bg-[#E04343] text-white px-4 py-2 rounded-md text-sm font-semibold">
                                + Add Rider
                            </button>
                        </div>
                    </div>

                    {/* ── Stats Cards ───────────────────────────────── */}
                    {stats && (
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 mb-5">
                            <div className="bg-white rounded-lg p-4 shadow-sm">
                                <p className="text-xs text-gray-500">Total Riders</p>
                                <p className="text-xl font-bold text-gray-800">{stats.total_riders}</p>
                            </div>
                            <div className="bg-white rounded-lg p-4 shadow-sm">
                                <p className="text-xs text-gray-500">Pending Review</p>
                                <p className="text-xl font-bold text-yellow-600">{stats.pending_review}</p>
                            </div>
                            <div className="bg-white rounded-lg p-4 shadow-sm">
                                <p className="text-xs text-gray-500">Approved</p>
                                <p className="text-xl font-bold text-green-600">{stats.approved}</p>
                            </div>
                            <div className="bg-white rounded-lg p-4 shadow-sm">
                                <p className="text-xs text-gray-500">Total Deliveries</p>
                                <p className="text-xl font-bold text-blue-600">{stats.total_deliveries}</p>
                            </div>
                            {stats.wallet && (
                                <div className="bg-white rounded-lg p-4 shadow-sm cursor-pointer hover:shadow-md transition" onClick={() => window.location.href = '/rider/wallet'}>
                                    <p className="text-xs text-gray-500">Wallet Balance</p>
                                    <p className="text-xl font-bold text-[#E04343]">{formatAmount(stats.wallet.available_balance)}</p>
                                    <p className="text-[10px] text-gray-400">Earned: {formatAmount(stats.wallet.total_earned)}</p>
                                    <p className="text-[10px] text-blue-500 mt-1">Click to manage wallet →</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── Filters ───────────────────────────────────── */}
                    <div className="flex gap-2 mb-4">
                        <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search rider name, email, phone, town..." className="flex-1 border border-gray-300 rounded-md p-2 text-sm" />
                        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="border border-gray-300 rounded-md p-2 text-sm min-w-[140px]">
                            <option value="all">All Status</option>
                            <option value="pending_review">Pending Review</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                        </select>
                        <button onClick={() => loadRiders()} className="bg-[#E04343] text-white px-4 py-2 rounded-md text-sm font-semibold">Search</button>
                    </div>

                    {/* ── Riders Table ──────────────────────────────── */}
                    <div className="relative overflow-x-auto">
                        <table className="w-full text-sm text-left text-gray-500 border-separate border-spacing-y-2">
                            <thead className="text-[11px] text-[#030229] bg-[#FAFAFA] uppercase">
                                <tr>
                                    <th className="px-2 py-2">S/N</th>
                                    <th className="px-2 py-2">Name</th>
                                    <th className="px-2 py-2">Email</th>
                                    <th className="px-2 py-2">Phone</th>
                                    <th className="px-2 py-2">Town</th>
                                    <th className="px-2 py-2">Status</th>
                                    <th className="px-2 py-2">Deliveries</th>
                                    <th className="px-4 py-2">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {riders?.data?.length > 0 ? riders.data.map((r: any, index: number) => (
                                    <tr key={r.id} className="bg-white">
                                        <td className="px-2 py-3 text-xs">{index + 1}</td>
                                        <td className="px-2 py-3">
                                            <div className="text-xs font-semibold">{r.user?.first_name} {r.user?.last_name}</div>
                                        </td>
                                        <td className="px-2 py-3 text-xs">{r.user?.email}</td>
                                        <td className="px-2 py-3 text-xs">{r.user?.phone_number}</td>
                                        <td className="px-2 py-3 text-xs">{r.rider_town || '—'}</td>
                                        <td className="px-2 py-3">
                                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${getStatusBadge(r.application_status)}`}>
                                                {r.application_status?.replace('_', ' ')}
                                            </span>
                                            {r.is_flagged && <span className="text-[10px] px-1.5 py-0.5 rounded font-bold bg-red-100 text-red-700 ml-1">FLAGGED</span>}
                                        </td>
                                        <td className="px-2 py-3 text-xs">
                                            {r.total_deliveries || 0}
                                            {r.disputed_deliveries > 0 && (
                                                <span className="text-red-500 ml-1">({r.disputed_deliveries} disputed)</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            <button onClick={() => viewDetail(r.user_id)} className="text-blue-600 hover:underline text-xs font-semibold">View</button>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan={8} className="text-center py-8 text-gray-400">
                                            {loader ? 'Loading...' : 'No riders found. Click "+ Add Rider" to create one.'}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* ── Pagination ────────────────────────────────── */}
                    <div className="flex justify-end mt-4">
                        {riders?.links?.map((link: any, idx: number) => (
                            <button key={idx} onClick={() => paginate(link.label)} disabled={link.active || !link.url}
                                className={`mx-0.5 py-1 px-2 text-xs ${link.active ? 'bg-[#E04343] text-white' : link.url ? 'bg-gray-200 text-black' : 'bg-gray-100 text-gray-400'}`}>
                                {link.label.replace('&laquo; Previous', '< Prev').replace('Next &raquo;', 'Next >')}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* ════════════════════════════════════════════════════ */}
            {/* ADD RIDER MODAL                                     */}
            {/* ════════════════════════════════════════════════════ */}
            <Modal visible={addModal} width="550" height="580" effect="fadeInUp" onClickAway={() => setAddModal(false)}>
                <div style={{ height: '100%', overflow: 'auto' }} className="p-4">
                    <div className="flex justify-between mb-3">
                        <p className="font-bold text-sm">Create New Rider</p>
                        <p className="cursor-pointer" onClick={() => setAddModal(false)}><SvgElement type={icontypesEnum.CANCEL} /></p>
                    </div>
                    <p className="text-xs text-gray-500 mb-4">This rider will be submitted for admin approval. Once approved, their delivery earnings will go to your wallet.</p>

                    <form onSubmit={handleCreateRider}>
                        <div className="grid grid-cols-2 gap-3">
                            {/* Required fields */}
                            <div>
                                <label className="text-xs text-gray-600 mb-1 block">First Name *</label>
                                <input type="text" name="first_name" value={riderForm.first_name} onChange={handleFormChange} className="w-full border border-gray-300 rounded-md p-2 text-sm" required />
                            </div>
                            <div>
                                <label className="text-xs text-gray-600 mb-1 block">Last Name *</label>
                                <input type="text" name="last_name" value={riderForm.last_name} onChange={handleFormChange} className="w-full border border-gray-300 rounded-md p-2 text-sm" required />
                            </div>
                            <div>
                                <label className="text-xs text-gray-600 mb-1 block">Email *</label>
                                <input type="email" name="email" value={riderForm.email} onChange={handleFormChange} className="w-full border border-gray-300 rounded-md p-2 text-sm" required />
                            </div>
                            <div>
                                <label className="text-xs text-gray-600 mb-1 block">Phone *</label>
                                <input type="text" name="phone_number" value={riderForm.phone_number} onChange={handleFormChange} className="w-full border border-gray-300 rounded-md p-2 text-sm" placeholder="+234..." required />
                            </div>

                            {/* Location */}
                            <div className="col-span-2">
                                <label className="text-xs text-gray-600 mb-1 block">Town / City * <span className="text-[10px] text-gray-400">(for metro rate matching)</span></label>
                                <input type="text" name="rider_town" value={riderForm.rider_town} onChange={handleFormChange} className="w-full border border-gray-300 rounded-md p-2 text-sm" placeholder="e.g., Lagos, Abuja" required />
                            </div>
                            <div>
                                <label className="text-xs text-gray-600 mb-1 block">State *</label>
                                <input type="text" name="state_of_residence" value={riderForm.state_of_residence} onChange={handleFormChange} className="w-full border border-gray-300 rounded-md p-2 text-sm" required />
                            </div>
                            <div>
                                <label className="text-xs text-gray-600 mb-1 block">LGA</label>
                                <input type="text" name="lga_of_residence" value={riderForm.lga_of_residence} onChange={handleFormChange} className="w-full border border-gray-300 rounded-md p-2 text-sm" />
                            </div>
                            <div className="col-span-2">
                                <label className="text-xs text-gray-600 mb-1 block">Address *</label>
                                <input type="text" name="residential_address" value={riderForm.residential_address} onChange={handleFormChange} className="w-full border border-gray-300 rounded-md p-2 text-sm" required />
                            </div>

                            {/* Optional KYC */}
                            <div>
                                <label className="text-xs text-gray-600 mb-1 block">Date of Birth</label>
                                <input type="date" name="date_of_birth" value={riderForm.date_of_birth} onChange={handleFormChange} className="w-full border border-gray-300 rounded-md p-2 text-sm" />
                            </div>
                            <div>
                                <label className="text-xs text-gray-600 mb-1 block">Gender</label>
                                <select name="gender" value={riderForm.gender} onChange={handleFormChange} className="w-full border border-gray-300 rounded-md p-2 text-sm">
                                    <option value="">-- Select --</option>
                                    <option value="male">Male</option>
                                    <option value="female">Female</option>
                                </select>
                            </div>
                            <div className="col-span-2">
                                <label className="text-xs text-gray-600 mb-1 block">NIN <span className="text-[10px] text-gray-400">(optional — admin can verify later)</span></label>
                                <input type="text" name="nin" maxLength={11} value={riderForm.nin} onChange={handleFormChange} className="w-full border border-gray-300 rounded-md p-2 text-sm" placeholder="11 digits" />
                            </div>

                            {/* Credentials */}
                            <div>
                                <label className="text-xs text-gray-600 mb-1 block">Password *</label>
                                <input type="password" name="password" value={riderForm.password} onChange={handleFormChange} className="w-full border border-gray-300 rounded-md p-2 text-sm" required />
                            </div>
                            <div>
                                <label className="text-xs text-gray-600 mb-1 block">Confirm Password *</label>
                                <input type="password" name="confirm_password" value={riderForm.confirm_password} onChange={handleFormChange} className="w-full border border-gray-300 rounded-md p-2 text-sm" required />
                            </div>
                        </div>

                        <div className="flex justify-end mt-5">
                            <button type="submit" disabled={actionLoader} className="bg-[#E04343] text-white px-6 py-2.5 rounded-full font-semibold text-sm flex items-center gap-2">
                                <span>Submit for Approval</span>
                                <Oval visible={actionLoader} height="16" width="16" color="#fff" secondaryColor="#FCBF94" ariaLabel="loading" />
                            </button>
                        </div>
                    </form>
                </div>
            </Modal>

            {/* ════════════════════════════════════════════════════ */}
            {/* RIDER DETAIL MODAL                                  */}
            {/* ════════════════════════════════════════════════════ */}
            <Modal visible={detailModal} width="550" height="500" effect="fadeInUp" onClickAway={() => { setDetailModal(false); setRiderDetail(null); setDetailRiderId(null); }}>
                <div style={{ height: '100%', overflow: 'auto' }} className="p-4">
                    <div className="flex justify-between mb-3">
                        <p className="font-bold text-sm">Rider Details</p>
                        <p className="cursor-pointer" onClick={() => { setDetailModal(false); setRiderDetail(null); setDetailRiderId(null); }}><SvgElement type={icontypesEnum.CANCEL} /></p>
                    </div>

                    {detailLoader ? (
                        <div className="flex justify-center py-10"><Oval visible height="30" width="30" color="#E04343" secondaryColor="#FCBF94" ariaLabel="loading" /></div>
                    ) : riderDetail ? (
                        <div>
                            {/* Profile Info */}
                            <div className="bg-gray-50 rounded-lg p-4 mb-4">
                                <h4 className="text-sm font-semibold text-gray-800 mb-2">{riderDetail.profile?.user?.first_name} {riderDetail.profile?.user?.last_name}</h4>
                                <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                                    <p>Email: {riderDetail.profile?.user?.email}</p>
                                    <p>Phone: {riderDetail.profile?.user?.phone_number}</p>
                                    <p>Town: {riderDetail.profile?.rider_town || '—'}</p>
                                    <p>State: {riderDetail.profile?.state_of_residence || '—'}</p>
                                    <p>Status: <span className={`px-1.5 py-0.5 rounded font-bold ${getStatusBadge(riderDetail.profile?.application_status)}`}>
                                        {riderDetail.profile?.application_status?.replace('_', ' ')}
                                    </span></p>
                                    {riderDetail.profile?.metro_rate && <p>Metro Rate: {formatAmount(riderDetail.profile.metro_rate)}</p>}
                                    {riderDetail.profile?.other_rate && <p>Other Rate: {formatAmount(riderDetail.profile.other_rate)}</p>}
                                </div>
                            </div>

                            {/* Delivery Stats */}
                            <div className="bg-gray-50 rounded-lg p-4 mb-4">
                                <h4 className="text-sm font-semibold text-gray-800 mb-2">Delivery Stats</h4>
                                <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                                    <p>Total: <strong>{riderDetail.delivery_stats?.total_deliveries || 0}</strong></p>
                                    <p>Successful: <strong className="text-green-600">{riderDetail.delivery_stats?.successful_deliveries || 0}</strong></p>
                                    <p>Disputed: <strong className="text-red-600">{riderDetail.delivery_stats?.disputed_deliveries || 0}</strong></p>
                                    <p>Failed: <strong className="text-orange-600">{riderDetail.delivery_stats?.failed_deliveries || 0}</strong></p>
                                </div>
                                {riderDetail.delivery_stats?.is_flagged && (
                                    <div className="mt-2 bg-red-50 border border-red-200 rounded p-2">
                                        <p className="text-xs text-red-700 font-semibold">⚠ FLAGGED: {riderDetail.delivery_stats.flag_reason}</p>
                                    </div>
                                )}
                            </div>

                            {/* Delivery Payments (Paginated) */}
                            {riderDetail.payments?.data?.length > 0 && (
                                <div className="bg-gray-50 rounded-lg p-4">
                                    <div className="flex justify-between items-center mb-2">
                                        <h4 className="text-sm font-semibold text-gray-800">Delivery Payments</h4>
                                        <span className="text-[10px] text-gray-400">
                                            Showing {riderDetail.payments.from}–{riderDetail.payments.to} of {riderDetail.payments.total}
                                        </span>
                                    </div>

                                    {paymentPageLoader ? (
                                        <div className="flex justify-center py-4">
                                            <Oval visible height="20" width="20" color="#E04343" secondaryColor="#FCBF94" ariaLabel="loading" />
                                        </div>
                                    ) : (
                                        <table className="w-full text-xs">
                                            <thead className="text-[10px] text-gray-500 uppercase">
                                                <tr>
                                                    <th className="text-left py-1">Shipment</th>
                                                    <th className="text-left py-1">Gross</th>
                                                    <th className="text-left py-1">Fine</th>
                                                    <th className="text-left py-1">Net</th>
                                                    <th className="text-left py-1">Rate</th>
                                                    <th className="text-left py-1">Status</th>
                                                    <th className="text-left py-1">Date</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {riderDetail.payments.data.map((p: any) => (
                                                    <tr key={p.id} className="border-t border-gray-200">
                                                        <td className="py-1.5 font-mono">{p.shipment_number}</td>
                                                        <td className="py-1.5">{formatAmount(p.gross_amount || p.amount)}</td>
                                                        <td className="py-1.5">
                                                            {p.fine_amount && Number(p.fine_amount) > 0 ? (
                                                                <span className="text-red-600 font-semibold">-{formatAmount(p.fine_amount)}</span>
                                                            ) : (
                                                                <span className="text-gray-300">—</span>
                                                            )}
                                                        </td>
                                                        <td className="py-1.5 font-semibold">{formatAmount(p.amount)}</td>
                                                        <td className="py-1.5">
                                                            <span className={`px-1 py-0.5 rounded text-[9px] font-bold ${p.rate_type === 'metro' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`}>
                                                                {p.rate_type}
                                                            </span>
                                                        </td>
                                                        <td className="py-1.5">
                                                            <span className={`px-1 py-0.5 rounded text-[9px] font-bold ${p.status === 'credited' ? 'bg-green-100 text-green-700' : p.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                                                {p.status?.replace('_', ' ')}
                                                            </span>
                                                        </td>
                                                        <td className="py-1.5">{p.delivery_date ? new Date(p.delivery_date).toLocaleDateString() : '—'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}

                                    {/* Payment Pagination */}
                                    {riderDetail.payments.last_page > 1 && (
                                        <div className="flex justify-end mt-3 gap-1">
                                            {riderDetail.payments.links?.map((link: any, idx: number) => (
                                                <button
                                                    key={idx}
                                                    onClick={() => {
                                                        if (link.url && detailRiderId) {
                                                            const page = link.label === 'Next &raquo;'
                                                                ? riderDetail.payments.current_page + 1
                                                                : link.label === '&laquo; Previous'
                                                                    ? riderDetail.payments.current_page - 1
                                                                    : parseInt(link.label);
                                                            loadDetailPayments(detailRiderId, page);
                                                        }
                                                    }}
                                                    disabled={!link.url || link.active}
                                                    className={`px-2 py-0.5 text-[10px] rounded ${link.active ? 'bg-[#E04343] text-white' : link.url ? 'bg-gray-200 text-black hover:bg-gray-300' : 'bg-gray-100 text-gray-400'}`}
                                                >
                                                    {link.label === '&laquo; Previous' ? '←' : link.label === 'Next &raquo;' ? '→' : link.label}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ) : (
                        <p className="text-center text-gray-400 py-10">No data available.</p>
                    )}
                </div>
            </Modal>

            <ToastContainer position="top-right" autoClose={2000} hideProgressBar />
        </>
    );
}

export default FleetRiders;